import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { camposFaltando, ehObrigatorio, MSG_OBRIGATORIOS } from '../utils/clienteObrigatorio';

const fmt = v => 'R$ ' + parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
const inpStyle = { width: '100%', padding: '9px 11px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', color: '#1C2533', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", boxSizing: 'border-box' };
const lbl = { color: '#6B7A8D', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.3px' };
const card = { background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '20px 22px', marginBottom: '14px' };

const FORMAS = ['Dinheiro', 'PIX', 'Cartão Crédito', 'Cartão Débito', 'Financiamento', 'Troca', 'Boleto'];

export default function NovaVenda() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [canais, setCanais] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const location = useLocation();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buscaVeiculo, setBuscaVeiculo] = useState('');
  const [buscaCliente, setBuscaCliente] = useState('');
  const [modalCliente, setModalCliente] = useState(false);
  const [novoCliente, setNovoCliente] = useState({});
  const [cepStatus, setCepStatus] = useState('');
  const [salvandoCliente, setSalvandoCliente] = useState(false);
  const [tentouSalvarCliente, setTentouSalvarCliente] = useState(false); // só destaca em vermelho depois da 1ª tentativa

  // Formas de pagamento múltiplas
  const [formasSelecionadas, setFormasSelecionadas] = useState([{ forma: '', valor: '', obs: '' }]);

  const [form, setForm] = useState({
    veiculo_id: '', cliente_id: '', origem_lead: '',
    valor_veiculo: '', valor_desconto: '0', valor_final: '',
    observacoes: '', transferencia_responsavel: 'cliente', valor_transferencia: '0',
    tem_garantia: true, data_garantia: '', km_garantia: '',
    tem_troca: false,
    veiculo_troca_marca: '', veiculo_troca_modelo: '', veiculo_troca_ano: '',
    veiculo_troca_cor: '', veiculo_troca_placa: '', veiculo_troca_renavam: '',
    veiculo_troca_chassi: '', veiculo_troca_combustivel: '',
    veiculo_troca_multas: false, veiculo_troca_ipva_pago: false,
  });

  const setV = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/veiculos').then(r => { const lista = r.data.filter(v => v.status === 'estoque'); setVeiculos(lista); if (location.state?.veiculo_id) { setV('veiculo_id', String(location.state.veiculo_id)); setStep(2); } });
    api.get('/clientes').then(r => setClientes(r.data));
    api.get('/vendas/canais').then(r => setCanais(r.data)).catch(() => setCanais([]));
  }, []);

  const veiculoSelecionado = veiculos.find(v => String(v.id) === String(form.veiculo_id));
  const clienteSelecionado = clientes.find(c => String(c.id) === String(form.cliente_id));

  const veiculosFiltrados = veiculos.filter(v =>
    `${v.marca} ${v.modelo} ${v.placa || ''}`.toLowerCase().includes(buscaVeiculo.toLowerCase())
  );
  const clientesFiltrados = clientes.filter(c =>
    c.nome?.toLowerCase().includes(buscaCliente.toLowerCase()) ||
    c.cpf_cnpj?.includes(buscaCliente)
  );

  // Cadastro rápido de cliente (modal da etapa 2)
  // Mesmos campos e comportamento do cadastro completo em Clientes.js (ViaCEP + código IBGE).
  const abrirModalCliente = () => { setNovoCliente({}); setCepStatus(''); setTentouSalvarCliente(false); setModalCliente(true); };
  const buscaCep = async (cep) => {
    const c = cep.replace(/[^0-9]/g, '');
    setNovoCliente(f => ({ ...f, cep }));
    if (c.length !== 8) { setCepStatus(''); return; }
    setCepStatus('Buscando CEP...');
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 6000);
    try {
      const r = await fetch('https://viacep.com.br/ws/' + c + '/json/', { signal: ctrl.signal });
      const d = await r.json();
      if (d.erro) { setCepStatus('CEP não encontrado — preencha o endereço manualmente'); return; }
      setNovoCliente(f => {
        if ((f.cep || '').replace(/[^0-9]/g, '') !== c) return f; // usuário já mudou o CEP
        return {
          ...f,
          endereco: d.logradouro || f.endereco,
          bairro: d.bairro || f.bairro,
          cidade: d.localidade || f.cidade,
          estado: d.uf || f.estado,
          cod_municipio_ibge: d.ibge || f.cod_municipio_ibge,
        };
      });
      setCepStatus(d.logradouro ? 'Endereço preenchido pelo CEP — confira e informe o número' : 'CEP geral da cidade — informe logradouro e bairro');
    } catch (e) {
      setCepStatus('Não foi possível consultar o CEP — preencha o endereço manualmente');
    } finally { clearTimeout(timer); }
  };
  // Cidade/UF alteradas à mão invalidam o código IBGE vindo do CEP (código errado bloqueia a NF-e).
  const setLocalCliente = (campo, valor) => setNovoCliente(f => ({ ...f, [campo]: valor, cod_municipio_ibge: '' }));
  const faltandoCliente = camposFaltando(novoCliente);
  const estiloCampoCliente = (campo) => tentouSalvarCliente && faltandoCliente.includes(campo) ? { ...inpStyle, border: '1px solid #C0392B', background: '#FDF2F1' } : inpStyle;
  const rotuloCliente = (label, campo) => <label style={lbl}>{label}{ehObrigatorio(campo, novoCliente) && <span style={{ color: '#C0392B' }}> *</span>}</label>;
  const inpCliente = (label, campo, tipo = 'text') => (
    <div style={{ marginBottom: '12px' }}>
      {rotuloCliente(label, campo)}
      <input type={tipo} value={novoCliente[campo] || ''} autoFocus={campo === 'nome'}
        onChange={e => setNovoCliente(f => ({ ...f, [campo]: e.target.value }))} style={estiloCampoCliente(campo)} />
    </div>
  );
  const salvarNovoCliente = async (e) => {
    e.preventDefault();
    if (faltandoCliente.length) { setTentouSalvarCliente(true); return; }
    setSalvandoCliente(true);
    try {
      const { data } = await api.post('/clientes', { ...novoCliente, nome: novoCliente.nome.trim() });
      setClientes(cs => [data, ...cs]);
      setBuscaCliente('');
      setV('cliente_id', String(data.id));
      setModalCliente(false);
    } catch (e) {
      alert('Erro ao cadastrar cliente: ' + (e.response?.data?.error || e.message));
    } finally {
      setSalvandoCliente(false);
    }
  };

  // Gerenciar formas de pagamento
  const addForma = () => setFormasSelecionadas(f => [...f, { forma: '', valor: '', obs: '' }]);
  const removeForma = (i) => setFormasSelecionadas(f => f.filter((_, idx) => idx !== i));
  const setForma = (i, campo, val) => setFormasSelecionadas(f => f.map((item, idx) => idx === i ? { ...item, [campo]: val } : item));

  const formasPagamentoTexto = formasSelecionadas
    .filter(f => f.forma)
    .map(f => { let t = f.valor ? `${f.forma} (${fmt(f.valor)})` : f.forma; if (f.obs) t += ` - ${f.obs}`; return t; })
    .join(' + ');

  // Valor do(s) veículo(s) dado(s) em troca (forma de pagamento "Troca")
  const valorVeiculoTroca = formasSelecionadas
    .filter(f => f.forma === 'Troca')
    .reduce((acc, f) => acc + (parseFloat(f.valor) || 0), 0);

  // Conferência das formas de pagamento x valor final da venda (em centavos, sem erro de float).
  // Só conta linhas com forma escolhida: valor sem forma não entra no texto salvo.
  const centavos = v => Math.round((parseFloat(v) || 0) * 100);
  const totalVendaCent = centavos(form.valor_final);
  const somaFormasCent = formasSelecionadas.filter(f => f.forma).reduce((acc, f) => acc + centavos(f.valor), 0);
  const diferencaCent = totalVendaCent - somaFormasCent; // > 0 falta, < 0 sobra
  const temForma = formasSelecionadas.some(f => f.forma);
  const pagamentoFechado = temForma && diferencaCent === 0;
  const motivoBloqueioPagamento = !temForma
    ? 'Selecione pelo menos uma forma de pagamento'
    : diferencaCent > 0 ? `A soma das formas de pagamento está ${fmt(diferencaCent / 100)} abaixo do valor da venda`
    : diferencaCent < 0 ? `A soma das formas de pagamento passa ${fmt(-diferencaCent / 100)} do valor da venda`
    : '';

  useEffect(() => {
    if (veiculoSelecionado) {
      setV('valor_veiculo', veiculoSelecionado.valor_venda);
      setV('valor_final', veiculoSelecionado.valor_venda);
    }
  }, [form.veiculo_id]);

  useEffect(() => {
    const vv = parseFloat(form.valor_veiculo || 0);
    const desc = parseFloat(form.valor_desconto || 0);
    setV('valor_final', String(Math.max(0, vv - desc)));
  }, [form.valor_veiculo, form.valor_desconto]);

  const inp = (label, campo, tipo = 'text', opts) => (
    <div style={{ marginBottom: '12px' }}>
      <label style={lbl}>{label}</label>
      {opts ? (
        <select value={form[campo] || ''} onChange={e => setV(campo, e.target.value)} style={inpStyle}>
          <option value=''>Selecione</option>
          {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : (
        <input type={tipo} value={form[campo] || ''} onChange={e => setV(campo, e.target.value)} style={inpStyle} />
      )}
    </div>
  );

  const salvar = async () => {
    if (!form.veiculo_id || !form.cliente_id || !form.valor_final) { alert('Preencha todos os campos obrigatórios'); return; }
    if (!form.origem_lead) { alert('Selecione a origem do lead'); setStep(2); return; }
    if (!pagamentoFechado) { alert(motivoBloqueioPagamento); setStep(4); return; }
    if (form.tem_troca && !(form.veiculo_troca_cor || '').trim()) { alert('Informe a cor do veículo de troca'); return; }
    setLoading(true);
    try {
      const { data: venda } = await api.post('/vendas', {
        ...form,
        forma_pagamento: formasPagamentoTexto,
        valor_veiculo_troca: valorVeiculoTroca,
      });
      // Ponte lead->venda: o backend sugere um lead aberto com o mesmo telefone.
      // Sugere-e-confirma: só vincula se o vendedor confirmar (nunca sozinho).
      if (venda.lead_sugerido) {
        const L = venda.lead_sugerido;
        const ok = window.confirm(
          `Encontramos um lead aberto com este telefone (${L.telefone}, status: ${L.status}).\n` +
          `Vincular esta venda a esse lead?`
        );
        if (ok) {
          try { await api.patch(`/leads/${L.id}`, { acao: 'vincular_venda', venda_id: venda.id }); }
          catch (e) { alert('Venda salva, mas não consegui vincular o lead: ' + (e.response?.data?.error || e.message)); }
        }
      }
      navigate('/vendas');
    } catch (err) { alert(err.response?.data?.error || 'Erro ao salvar venda'); }
    finally { setLoading(false); }
  };

  const steps = ['Veículo', 'Cliente', 'Valores', 'Pagamento', 'Confirmar'];

  return (
    <div style={{ maxWidth: '700px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#1C2533' }}>Nova Venda</div>
        <button onClick={() => navigate('/vendas')} style={{ background: 'none', border: 'none', color: '#6B7A8D', cursor: 'pointer', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>× Cancelar</button>
      </div>

      {/* Steps */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
        {steps.map((s, i) => (
          <div key={s} style={{ flex: 1, textAlign: 'center', padding: '10px 4px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', background: step === i + 1 ? '#1A6DFF' : step > i + 1 ? '#EAF5EE' : '#fff', cursor: step > i + 1 ? 'pointer' : 'default' }}
            onClick={() => step > i + 1 && setStep(i + 1)}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: step === i + 1 ? '#fff' : step > i + 1 ? '#2D7D4F' : '#9AA3AE' }}>{i + 1}. {s}</div>
          </div>
        ))}
      </div>

      {/* Step 1 — Veículo */}
      {step === 1 && (
        <div style={card}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1C2533', marginBottom: '14px' }}>Selecionar veículo</div>
          <input placeholder='Buscar por marca, modelo ou placa...' value={buscaVeiculo} onChange={e => setBuscaVeiculo(e.target.value)} style={{ ...inpStyle, marginBottom: '12px' }} />
          <div style={{ maxHeight: '320px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {veiculosFiltrados.map(v => (
              <div key={v.id} onClick={() => { setV('veiculo_id', String(v.id)); }}
                style={{ padding: '12px 14px', borderRadius: '8px', border: `1.5px solid ${String(v.id) === String(form.veiculo_id) ? '#1A3A5C' : 'rgba(26,58,92,0.12)'}`, background: String(v.id) === String(form.veiculo_id) ? '#E6F1FB' : '#fff', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#1C2533' }}>{v.marca} {v.modelo} {v.ano_mod}</div>
                  <div style={{ fontSize: '12px', color: '#9AA3AE', marginTop: '2px' }}>{v.placa || 'S/Placa'} · {v.cor}</div>
                </div>
                <div style={{ fontWeight: '700', fontFamily: "'DM Mono',monospace", color: '#2D7D4F', fontSize: '14px' }}>{fmt(v.valor_venda)}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
            <button onClick={() => setStep(2)} disabled={!form.veiculo_id}
              style={{ padding: '9px 20px', background: form.veiculo_id ? '#1A6DFF' : '#F4F6F9', border: 'none', color: form.veiculo_id ? '#fff' : '#9AA3AE', borderRadius: '8px', cursor: form.veiculo_id ? 'pointer' : 'not-allowed', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
              Próximo →
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — Cliente */}
      {step === 2 && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#1C2533' }}>Selecionar cliente</div>
            <button onClick={abrirModalCliente}
              style={{ padding: '7px 14px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>+ Cadastrar cliente</button>
          </div>
          <input placeholder='Buscar por nome ou CPF...' value={buscaCliente} onChange={e => setBuscaCliente(e.target.value)} style={{ ...inpStyle, marginBottom: '12px' }} />
          <div style={{ maxHeight: '320px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {clientesFiltrados.map(c => (
              <div key={c.id} onClick={() => setV('cliente_id', String(c.id))}
                style={{ padding: '12px 14px', borderRadius: '8px', border: `1.5px solid ${String(c.id) === String(form.cliente_id) ? '#1A3A5C' : 'rgba(26,58,92,0.12)'}`, background: String(c.id) === String(form.cliente_id) ? '#E6F1FB' : '#fff', cursor: 'pointer' }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1C2533' }}>{c.nome}</div>
                <div style={{ fontSize: '12px', color: '#9AA3AE', marginTop: '2px' }}>{c.cpf_cnpj} · {c.telefone}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '0.5px solid rgba(26,58,92,0.1)' }}>
            <label style={lbl}>Origem do Lead <span style={{ color: '#C0392B' }}>*</span></label>
            <select value={form.origem_lead || ''} onChange={e => setV('origem_lead', e.target.value)} style={inpStyle}>
              <option value=''>Selecione o canal de origem...</option>
              {canais.map(o => (
                <option key={o.id} value={o.nome}>{o.nome}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '14px' }}>
            <button onClick={() => setStep(1)} style={{ padding: '9px 18px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>← Voltar</button>
            <button onClick={() => setStep(3)} disabled={!form.cliente_id || !form.origem_lead}
              style={{ padding: '9px 20px', background: (form.cliente_id && form.origem_lead) ? '#1A6DFF' : '#F4F6F9', border: 'none', color: (form.cliente_id && form.origem_lead) ? '#fff' : '#9AA3AE', borderRadius: '8px', cursor: (form.cliente_id && form.origem_lead) ? 'pointer' : 'not-allowed', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
              Próximo →
            </button>
          </div>
        </div>
      )}

      {/* Modal — Cadastro de cliente (mesmos campos de Clientes.js) */}
      {modalCliente && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '520px', maxWidth: '100%', maxHeight: '88vh', overflowY: 'auto', boxSizing: 'border-box', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', margin: 0 }}>Cadastrar cliente</h3>
              <button type='button' onClick={() => !salvandoCliente && setModalCliente(false)} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9AA3AE', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={salvarNovoCliente}>
              {inpCliente('Nome Completo', 'nome')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                {inpCliente('CPF/CNPJ', 'cpf_cnpj')} {inpCliente('RG', 'rg')}
                {inpCliente('Data de Nascimento', 'data_nascimento', 'date')} {inpCliente('Telefone', 'telefone')}
                {inpCliente('Telefone 2', 'telefone2')}
              </div>
              {inpCliente('Email', 'email', 'email')}
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0 16px', alignItems: 'end' }}>
                <div style={{ marginBottom: '12px' }}>
                  {rotuloCliente('CEP', 'cep')}
                  <input value={novoCliente.cep || ''} onChange={e => buscaCep(e.target.value)} placeholder='00000-000' style={estiloCampoCliente('cep')} />
                </div>
                <div style={{ marginBottom: '12px', fontSize: '12px', color: '#6B7A8D', paddingBottom: '9px' }}>{cepStatus}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '0 16px' }}>
                {inpCliente('Logradouro', 'endereco')} {inpCliente('Número', 'numero')}
              </div>
              {inpCliente('Bairro', 'bairro')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 110px', gap: '0 16px' }}>
                <div style={{ marginBottom: '12px' }}>
                  {rotuloCliente('Cidade', 'cidade')}
                  <input value={novoCliente.cidade || ''} onChange={e => setLocalCliente('cidade', e.target.value)} style={estiloCampoCliente('cidade')} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  {rotuloCliente('UF', 'estado')}
                  <input value={novoCliente.estado || ''} onChange={e => setLocalCliente('estado', e.target.value.toUpperCase())} maxLength={2} style={estiloCampoCliente('estado')} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Cód. IBGE</label>
                  <input value={novoCliente.cod_municipio_ibge || ''} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 7); setNovoCliente(f => ({ ...f, cod_municipio_ibge: v })); }} placeholder='7 dígitos' style={inpStyle} />
                </div>
              </div>
              {tentouSalvarCliente && faltandoCliente.length > 0 && (
                <div style={{ color: '#C0392B', fontSize: '12px', fontWeight: '600', marginTop: '4px' }}>{MSG_OBRIGATORIOS}</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
                <button type='button' onClick={() => setModalCliente(false)} disabled={salvandoCliente}
                  style={{ padding: '9px 18px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
                <button type='submit' disabled={salvandoCliente} aria-disabled={faltandoCliente.length > 0} title={faltandoCliente.length ? MSG_OBRIGATORIOS : ''}
                  style={{ padding: '9px 20px', background: faltandoCliente.length ? '#B8C2CF' : '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: salvandoCliente ? 'wait' : faltandoCliente.length ? 'not-allowed' : 'pointer', fontWeight: '600', opacity: salvandoCliente ? 0.7 : 1, fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                  {salvandoCliente ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Step 3 — Valores */}
      {step === 3 && (
        <div style={card}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1C2533', marginBottom: '14px' }}>Valores</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {inp('Valor do veículo', 'valor_veiculo', 'number')}
            {inp('Desconto', 'valor_desconto', 'number')}
          </div>
          <div style={{ background: '#EAF5EE', borderRadius: '8px', padding: '14px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#2D7D4F' }}>Valor final</span>
            <span style={{ fontSize: '22px', fontWeight: '900', fontFamily: "'DM Mono',monospace", color: '#2D7D4F' }}>{fmt(form.valor_final)}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {inp('Transferência por', 'transferencia_responsavel', 'text', [{ v: 'cliente', l: 'Cliente' }, { v: 'loja', l: 'Loja' }])}
            {inp('Valor Transferência', 'valor_transferencia', 'number')}
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ color: '#6B7A8D', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type='checkbox' checked={form.tem_garantia} onChange={e => setV('tem_garantia', e.target.checked)} />
              Incluir garantia
            </label>
          </div>
          {form.tem_garantia && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              {inp('Data garantia', 'data_garantia', 'date')}
              {inp('KM garantia', 'km_garantia', 'number')}
            </div>
          )}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ color: '#6B7A8D', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type='checkbox' checked={form.tem_troca} onChange={e => setV('tem_troca', e.target.checked)} />
              Veículo de troca
            </label>
          </div>
          {form.tem_troca && (
            <div style={{ background: '#F4F6F9', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7A8D', marginBottom: '10px', textTransform: 'uppercase' }}>Dados da troca</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                {inp('Marca', 'veiculo_troca_marca')} {inp('Modelo', 'veiculo_troca_modelo')}
                {inp('Ano', 'veiculo_troca_ano')} {inp('Cor *', 'veiculo_troca_cor')}
                {inp('Placa', 'veiculo_troca_placa')} {inp('Renavam', 'veiculo_troca_renavam')}
                {inp('Chassi', 'veiculo_troca_chassi')}
                {inp('Combustível', 'veiculo_troca_combustivel', 'text', [{ v: 'Gasolina', l: 'Gasolina' }, { v: 'Flex', l: 'Flex' }, { v: 'Etanol', l: 'Etanol' }, { v: 'Eletrico', l: 'Elétrico' }])}
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <label style={{ fontSize: '12px', color: '#6B7A8D', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type='checkbox' checked={form.veiculo_troca_multas} onChange={e => setV('veiculo_troca_multas', e.target.checked)} /> Com multas
                </label>
                <label style={{ fontSize: '12px', color: '#6B7A8D', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type='checkbox' checked={form.veiculo_troca_ipva_pago} onChange={e => setV('veiculo_troca_ipva_pago', e.target.checked)} /> IPVA pago
                </label>
              </div>
            </div>
          )}
          {inp('Observações', 'observacoes')}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
            <button onClick={() => setStep(2)} style={{ padding: '9px 18px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>← Voltar</button>
            <button onClick={() => setStep(4)} disabled={!form.valor_final}
              style={{ padding: '9px 20px', background: form.valor_final ? '#1A6DFF' : '#F4F6F9', border: 'none', color: form.valor_final ? '#fff' : '#9AA3AE', borderRadius: '8px', cursor: form.valor_final ? 'pointer' : 'not-allowed', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
              Próximo →
            </button>
          </div>
        </div>
      )}

      {/* Step 4 — Pagamento */}
      {step === 4 && (
        <div style={card}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1C2533', marginBottom: '6px' }}>Formas de pagamento</div>
          <div style={{ fontSize: '12px', color: '#9AA3AE', marginBottom: '16px' }}>Você pode combinar mais de uma forma. A soma dos valores precisa fechar com o valor da venda.</div>

          {formasSelecionadas.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '10px' }}>
              <div style={{ flex: 2 }}>
                {i === 0 && <label style={lbl}>Forma</label>}
                <select value={item.forma} onChange={e => setForma(i, 'forma', e.target.value)} style={inpStyle}>
                  <option value=''>Selecione</option>
                  {FORMAS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                {i === 0 && <label style={lbl}>Valor</label>}
                <input type='number' placeholder='R$ 0,00' value={item.valor} onChange={e => setForma(i, 'valor', e.target.value)} style={inpStyle} />
              </div>
              <div style={{ flex: 2 }}>
                {i === 0 && <label style={lbl}>Observação (opcional)</label>}
                <input type='text' placeholder='Ex: parcela 1, entrada...' value={item.obs} onChange={e => setForma(i, 'obs', e.target.value)} style={inpStyle} />
              </div>
              {formasSelecionadas.length > 1 && (
                <button onClick={() => removeForma(i)}
                  style={{ padding: '8px 10px', background: '#FDECEA', border: 'none', color: '#C0392B', borderRadius: '7px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '700', marginBottom: '1px' }}>
                  ×
                </button>
              )}
            </div>
          ))}

          <button onClick={addForma}
            style={{ padding: '8px 14px', background: 'transparent', border: '1px dashed rgba(26,58,92,0.3)', color: '#1A3A5C', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", marginBottom: '16px', width: '100%' }}>
            + Adicionar outra forma de pagamento
          </button>

          {formasPagamentoTexto && (
            <div style={{ background: '#E6F1FB', borderRadius: '8px', padding: '12px 14px', marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', color: '#6B7A8D', fontWeight: '600', marginBottom: '4px' }}>RESUMO DO PAGAMENTO</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3A5C' }}>{formasPagamentoTexto}</div>
            </div>
          )}

          <div style={{ border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '8px', padding: '12px 14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6B7A8D', marginBottom: '6px' }}>
              <span>Valor total da venda</span>
              <span style={{ fontFamily: "'DM Mono',monospace", color: '#1C2533', fontWeight: '600' }}>{fmt(totalVendaCent / 100)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6B7A8D', marginBottom: '8px' }}>
              <span>Soma das formas de pagamento</span>
              <span style={{ fontFamily: "'DM Mono',monospace", color: '#1C2533', fontWeight: '600' }}>{fmt(somaFormasCent / 100)}</span>
            </div>
            <div style={{ paddingTop: '8px', borderTop: '0.5px solid rgba(26,58,92,0.1)', textAlign: 'right', fontSize: '14px', fontWeight: '700',
              color: diferencaCent > 0 ? '#C0392B' : diferencaCent < 0 ? '#E67E22' : '#2D7D4F' }}>
              {diferencaCent > 0 ? `Faltam ${fmt(diferencaCent / 100)}` : diferencaCent < 0 ? `${fmt(-diferencaCent / 100)} a mais` : 'Valor fechado ✓'}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={() => setStep(3)} style={{ padding: '9px 18px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>← Voltar</button>
            {/* title no span: botão disabled não dispara hover em todo navegador */}
            <span title={motivoBloqueioPagamento || undefined}>
              <button onClick={() => setStep(5)} disabled={!pagamentoFechado}
                style={{ padding: '9px 20px', background: pagamentoFechado ? '#1A6DFF' : '#F4F6F9', border: 'none', color: pagamentoFechado ? '#fff' : '#9AA3AE', borderRadius: '8px', cursor: pagamentoFechado ? 'pointer' : 'not-allowed', pointerEvents: pagamentoFechado ? 'auto' : 'none', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                Próximo →
              </button>
            </span>
          </div>
        </div>
      )}

      {/* Step 5 — Confirmar */}
      {step === 5 && (
        <div style={card}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1C2533', marginBottom: '16px' }}>Confirmar venda</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
            {[
              { label: 'Veículo', val: veiculoSelecionado ? `${veiculoSelecionado.marca} ${veiculoSelecionado.modelo} ${veiculoSelecionado.ano_mod}` : '—' },
              { label: 'Cliente', val: clienteSelecionado?.nome || '—' },
              { label: 'Valor veículo', val: fmt(form.valor_veiculo) },
              { label: 'Desconto', val: fmt(form.valor_desconto) },
              { label: 'Transferência', val: `${form.transferencia_responsavel} · ${fmt(form.valor_transferencia)}` },
              { label: 'Garantia', val: form.tem_garantia ? 'Sim' : 'Não' },
            ].map(item => (
              <div key={item.label} style={{ background: '#F4F6F9', borderRadius: '8px', padding: '10px 12px' }}>
                <div style={{ fontSize: '10px', color: '#9AA3AE', fontWeight: '600', textTransform: 'uppercase', marginBottom: '3px' }}>{item.label}</div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#1C2533' }}>{item.val}</div>
              </div>
            ))}
          </div>
          <div style={{ background: '#EAF5EE', borderRadius: '8px', padding: '14px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '11px', color: '#2D7D4F', fontWeight: '600', marginBottom: '3px' }}>VALOR FINAL</div>
              <div style={{ fontSize: '22px', fontWeight: '700', color: '#2D7D4F', fontFamily: "'DM Mono',monospace" }}>{fmt(form.valor_final)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: '#6B7A8D', fontWeight: '600', marginBottom: '3px' }}>PAGAMENTO</div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1A3A5C' }}>{formasPagamentoTexto}</div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
            <button onClick={() => setStep(4)} style={{ padding: '9px 18px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>← Voltar</button>
            <button onClick={salvar} disabled={loading}
              style={{ padding: '10px 24px', background: '#2D7D4F', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
              {loading ? 'Salvando...' : '✓ Confirmar Venda'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
